import React, { FC, useState, useEffect } from "react";
import { useParams } from "react-router";
import styled from "@emotion/styled";
import { Button } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { uuidModule } from "@/modules/uuid";
import { Uuid } from "@/domains/global/identifiers";
import {
  SessionEventSummarySchema,
  UserActionEventValue,
  NoteSessionMention,
  CollectionSessionMention,
  SessionMention,
} from "@/domains/openapi/types";
import { SessionEventMessage } from "./SessionEventMessage";
import { ChatInput } from "./ChatInput";
import { TriggerProcessingModal } from "./TriggerProcessingModal";
import { ExtendTimeoutModal } from "./ExtendTimeoutModal";
import { RawEventsModal } from "./RawEventsModal";
import { AgentRunsModal } from "./AgentRunsModal";
import { TERMINAL_FONT_FAMILY } from "./shared/constants";
import { useSessionEvents } from "@/contexts/session-events";

interface SelectedItem {
  id: string;
  kind: "NOTE" | "COLLECTION";
  label: string;
}

// Helper function to get status color
const getStatusColor = (status: string | null | undefined): string => {
  switch (status) {
    case "PROCESSING":
      return "var(--blue-11)";
    case "FINISHED":
      return "var(--green-11)";
    case "ERROR":
      return "var(--red-11)";
    case "DISMISSED":
      return "var(--gray-11)";
    default:
      return "var(--gray-10)";
  }
};

// Convert Electric session events to the format expected by existing components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertElectricEventToSessionEvent = (electricEvent: any): SessionEventSummarySchema => {
  console.log("electricEvent", electricEvent);
  return {
    id: electricEvent.id,
    session_id: electricEvent.session_id,
    locally_created_at: electricEvent.locally_created_at,
    internal_created_at: electricEvent.created_at,
    internal_updated_at: electricEvent.updated_at,
    data: {
      kind: electricEvent.event_kind,
      value: electricEvent.value_json,
    },
  } as SessionEventSummarySchema;
};

export const SessionEventsPage: FC = () => {
  const { sessionId } = useParams<{ sessionId: Uuid }>();
  const {
    sessionData,
    sortedEvents,
    isSubscribed,
    isReady,
    subscriptionError,
    subscribeToSession,
    unsubscribeFromSession,
  } = useSessionEvents();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExtendingTimeout, setIsExtendingTimeout] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showExtendTimeoutModal, setShowExtendTimeoutModal] = useState(false);
  const [showRawModal, setShowRawModal] = useState(false);
  const [showAgentRunsModal, setShowAgentRunsModal] = useState(false);

  if (!sessionId) {
    return <div>Invalid Session ID</div>;
  }

  // Subscribe to session on mount and when sessionId changes
  useEffect(() => {
    const initializeSubscription = async () => {
      try {
        await subscribeToSession(sessionId);
      } catch (error) {
        console.error("Failed to subscribe to session:", error);
      }
    };

    initializeSubscription();

    // Cleanup on unmount or sessionId change
    return () => {
      unsubscribeFromSession();
    };
  }, [sessionId, subscribeToSession, unsubscribeFromSession]);

  // Convert Electric events to the format expected by existing components
  const sessionEvents = sortedEvents.map(convertElectricEventToSessionEvent);

  const handleSendMessage = async (message: string, contexts?: SelectedItem[]) => {
    if (!sessionData?.owned_by_space_account_id) {
      throw new Error("Unable to get session owner space account ID");
    }

    setIsSubmitting(true);
    setCreateError(null);

    try {
      // Build the value object with parts and optionally contexts
      const eventValue: UserActionEventValue = {
        parts: [{ kind: "MARKDOWN_V1", id: uuidModule.generate(), content: message }],
      };

      // Add context if provided
      if (contexts && contexts.length > 0) {
        console.log("Debug: contexts received:", contexts);

        const mentions: SessionMention[] = contexts.map(item => {
          if (item.kind === "NOTE") {
            const mention: NoteSessionMention = {
              kind: "NOTE",
              note_id: item.id,
              note_primary_label: item.label,
            };
            console.log("Debug: created note mention:", mention);
            return mention;
          } else {
            const mention: CollectionSessionMention = {
              kind: "COLLECTION",
              collection_id: item.id,
              collection_title: item.label,
            };
            console.log("Debug: created collection mention:", mention);
            return mention;
          }
        });

        eventValue.context = {
          kind: "USER_CONTEXT_V1",
          mentions,
        };

        console.log("Debug: final eventValue:", eventValue);
      }

      await api.post("/internal/v1/session-events", {
        body: {
          session_id: sessionId,
          kind: "USER_ACTION_V1",
          owner_space_account_id: sessionData.owned_by_space_account_id,
          value: eventValue,
        },
      });

      // Note: No need to manually refresh since Electric SQL provides real-time updates
    } catch (err) {
      throw new Error(
        `Failed to send message: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    setDeletingEventId(eventId);
    try {
      await api.delete("/internal/v1/session-events/{session_event_id}", {
        params: {
          path: {
            session_event_id: eventId,
          },
        },
      });

      // Note: Electric SQL will automatically update the events list
    } catch (err) {
      alert(`Failed to delete event: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleProcessSession = async (params: {
    timeout_seconds?: number;
    immediate?: boolean;
  }) => {
    setIsProcessing(true);
    try {
      await api.post("/v2/sessions/{session_id}/process", {
        params: {
          path: {
            session_id: sessionId,
          },
        },
        body: params,
      });

      // Note: Electric SQL will automatically update the session data
    } catch (err) {
      alert(
        `Failed to start session processing: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExtendTimeout = async (params: { timeout_seconds?: number }) => {
    setIsExtendingTimeout(true);
    try {
      await api.post("/v2/sessions/{session_id}/extend-timeout", {
        params: {
          path: {
            session_id: sessionId,
          },
        },
        body: {
          timeout_seconds: params.timeout_seconds ?? 0,
        },
      });

      // Note: Electric SQL will automatically update the session data
    } catch (err) {
      alert(
        `Failed to extend session timeout: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setIsExtendingTimeout(false);
    }
  };

  // Loading state
  if (!isReady && !subscriptionError) {
    return (
      <Container>
        <div>Loading session events...</div>
      </Container>
    );
  }

  // Error state
  if (subscriptionError) {
    return (
      <Container>
        <div>Error connecting to session events: {subscriptionError}</div>
        <Button onClick={() => subscribeToSession(sessionId)} style={{ marginTop: "1rem" }}>
          Retry Connection
        </Button>
      </Container>
    );
  }

  return (
    <Container>
      <TerminalHeader>
        <TerminalPrompt>Session Event History</TerminalPrompt>
        <StatusLine>
          session_id={sessionId} status=
          <StatusIndicator status={sessionData?.status}>
            {sessionData?.status ?? "unknown"}
          </StatusIndicator>{" "}
          events={sessionEvents.length}
          {isSubscribed && <ConnectedIndicator> • Real-time Connected</ConnectedIndicator>}
        </StatusLine>
        <HeaderControls>
          <TerminalButton size="1" variant="ghost" onClick={() => setShowTriggerModal(true)}>
            Process Session
          </TerminalButton>
          <TerminalButton size="1" variant="ghost" onClick={() => setShowExtendTimeoutModal(true)}>
            Extend Timeout
          </TerminalButton>
          <TerminalButton size="1" variant="ghost" onClick={() => setShowRawModal(true)}>
            View Raw Events
          </TerminalButton>
          <TerminalButton
            size="1"
            variant="ghost"
            onClick={() => {
              const temporalUrl = `https://cloud.temporal.io/namespaces/prod-backend-async-worker.rqp4d/workflows/ProcessSessionWorkflow-${sessionId}`;
              window.open(temporalUrl, "_blank");
            }}
          >
            View Temporal Workflow
          </TerminalButton>
          <TerminalButton
            size="1"
            variant="ghost"
            onClick={() => {
              const sessionAgentId = `session_agent_${sessionId}`.toLowerCase();
              const logfireUrl = `https://logfire-us.pydantic.dev/mem/backend?q=message+LIKE+%27${sessionAgentId}%25+run%27&last=1d`;
              window.open(logfireUrl, "_blank");
            }}
          >
            Logfire
          </TerminalButton>
          <TerminalButton size="1" variant="ghost" onClick={() => setShowAgentRunsModal(true)}>
            View Agent Runs
          </TerminalButton>
        </HeaderControls>
      </TerminalHeader>

      <TerminalOutput>
        {sessionEvents.length === 0 ? (
          <EmptyState>$ waiting for events...</EmptyState>
        ) : (
          sessionEvents.map((event: SessionEventSummarySchema) => (
            <SessionEventMessage
              key={event.id}
              event={event}
              onDelete={handleDeleteEvent}
              isDeleting={deletingEventId === event.id}
            />
          ))
        )}
      </TerminalOutput>

      <ChatInput onSendMessage={handleSendMessage} isSubmitting={isSubmitting} />

      {createError && <ErrorLine>error: {createError}</ErrorLine>}

      <TriggerProcessingModal
        isOpen={showTriggerModal}
        onClose={() => setShowTriggerModal(false)}
        onTrigger={handleProcessSession}
        isProcessing={isProcessing}
      />

      <ExtendTimeoutModal
        isOpen={showExtendTimeoutModal}
        onClose={() => setShowExtendTimeoutModal(false)}
        onExtend={handleExtendTimeout}
        isExtending={isExtendingTimeout}
      />

      <RawEventsModal
        isOpen={showRawModal}
        onClose={() => setShowRawModal(false)}
        events={sessionEvents}
      />

      <AgentRunsModal
        isOpen={showAgentRunsModal}
        onClose={() => setShowAgentRunsModal(false)}
        sessionId={sessionId}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  font-family: ${TERMINAL_FONT_FAMILY};
  min-height: 80vh;
  padding: 20px;
  font-size: 13px;
  line-height: 1.4;
  max-width: 1200px;
  overflow-y: scroll;
`;

const TerminalHeader = styled.div`
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--gray-6);
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const TerminalPrompt = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--green-11);
  font-family: ${TERMINAL_FONT_FAMILY};
`;

const StatusLine = styled.div`
  font-size: 13px;
  color: var(--gray-10);
  font-family: ${TERMINAL_FONT_FAMILY};
  margin-top: 2px;
`;

const ConnectedIndicator = styled.span`
  color: var(--green-11);
  font-weight: 600;
`;

const TerminalOutput = styled.div`
  margin-bottom: 26px;
  font-family: ${TERMINAL_FONT_FAMILY};
`;

const EmptyState = styled.div`
  color: var(--gray-10);
  font-size: 13px;
  padding: 20px 0;
  font-family: ${TERMINAL_FONT_FAMILY};
  font-style: italic;
`;

const ErrorLine = styled.div`
  color: var(--red-11);
  font-size: 13px;
  font-family: ${TERMINAL_FONT_FAMILY};
  margin-bottom: 8px;
  padding: 4px 0;
`;

const TerminalButton = styled(Button)`
  font-size: 13px;
  font-family: ${TERMINAL_FONT_FAMILY};
  min-height: auto;
  height: auto;
  padding: 2px 8px;
  text-decoration: underline;
`;

const StatusIndicator = styled.span<{ status: string | null | undefined }>`
  color: ${({ status }) => getStatusColor(status)};
  font-weight: 600;
  font-size: 13px;
  font-family: ${TERMINAL_FONT_FAMILY};
`;
